import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import closeIcon from "assets/icons/x-circle.svg";
import styles from './FileUploadWithPreview.module.scss';

type Props = {
  image?: string | File;
  onAddImage?: (e: File) => void;
  onRemoveImage?: () => void;
  alt?: string;
  disabled?: boolean;
};


export const FileUploadWithPreview: React.FC<Props> = ({ image, alt, onAddImage, onRemoveImage, disabled }) => {
  const [img, setImg] = useState<string | null>(null);

  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] as File;

    if (!file) return;

    setImg(createImageUrl(file));
  }

  const handleRemoveImage = () => {
    const currentUrl = img as string;

    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
      setImg(null);
    }
  }

  const createImageUrl = (file: File) => {
    return URL.createObjectURL(file);
  }



  useEffect(() => {
    if (typeof image === 'string') {
      setImg(image);
    }

    if (image instanceof File) {
      setImg(createImageUrl(image));
    }

    return () => {
      if (typeof image === 'string') {
        URL.revokeObjectURL(image);
      }
    }

  }, [image]);

  return (
    <div className={styles['file-upload']}>
      {img && (
        <>
        <div className={styles['image-container']}>
          <img
            src={img || ''}
            alt={alt}
            className={styles['image']}
          />
        </div>
        {!disabled && (
          <button
            type="button"
            className={styles['close-button']}
            onClick={handleRemoveImage}
          >
            <img src={closeIcon} alt="Close icon" />
          </button>
        )}  
        </>
      )}
      {!disabled && (
        <label className={styles['label']}>
          <span>Select Image</span>
          <input
            className={styles['input']}
            type='file'
            onChange={handleAddImage}
            accept="image/*"
          />
        </label>
      )}
    </div>
  );
};

export const UploadImageWithPreview = forwardRef<{}, Props>(({
  image,
  disabled
}, ref) => {
  const [img, setImg] = useState<string | null>(null);

  const fileRef = useRef<File | null>(null);

  useImperativeHandle(ref, () => ({
    getImage: () => fileRef.current
  }));

  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] as File;

    if (!file) return;

    fileRef.current = file;
    setImg(createImageUrl(file));

    e.target.value = '';
  }

  const handleRemoveImage = () => {
    const currentUrl = img as string;
    
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
      fileRef.current = null;
      setImg(null);
    }
  }

  const createImageUrl = (file: File) => {
    return URL.createObjectURL(file);
  }

  useEffect(() => {
    if (typeof image === 'string') {
      setImg(image);
    }

    if (image instanceof File) {
      setImg(createImageUrl(image));
    }

    return () => {
      if (typeof image === 'string') {
        URL.revokeObjectURL(image);
      }
    }

  }, [image]);

  return (
    <div className={styles['file-upload']}>
      {img && (
        <>
        <div className={styles['image-container']}>
          <img
            src={img || ''}
            alt='Preview Image'
            className={styles['image']}
          />
        </div>
        {!disabled && (
          <button
            type="button"
            className={styles['close-button']}
            onClick={handleRemoveImage}
          >
            <img src={closeIcon} alt="Close icon" />
          </button>
        )}
        </>
      )}
      {!disabled && (
        <label className={styles['label']}>
          <span>Select Image</span>
          <input
            className={styles['input']}
            type='file'
            onChange={handleAddImage}
            accept="image/*"
          />
        </label>
      )}
    </div>
  );
});